import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, CardBody, Typography } from '@material-tailwind/react';
import { rootUrl } from '../../../config/config';
import { DEFAULT_COMPANY_INFO } from '../../../utils/companyInfo';
import DocumentPreviewScaler from './DocumentPreviewScaler';
import OwnerTransactionPrintSheet from './OwnerTransactionPrintSheet';
import { buildOwnerTransactionFilename, generateOwnerTransactionPdf } from '../../../utils/ownerTransactionPDFGenerator';

const getDocumentLabel = (transaction) => {
  const type = String(transaction?.trans_type || '').toUpperCase();
  const labels = {
    REM: 'Remittance',
    PAY: 'Payment',
    RIM: 'Reimbursement',
    CHG: 'Charge',
    ADJ: 'Adjustment',
    MOI: 'Monthly Invoice',
    JIN: 'Job Card Invoice',
    REV: 'Reversal'
  };

  return labels[type] || transaction?.short_desc || type || 'Owner Transaction';
};

const isReversedDocument = (transaction) => (
  transaction?.reversed === true
  || transaction?.reversed === 1
  || transaction?.reversed === '1'
  || String(transaction?.reference_type || '').toUpperCase() === 'REVERSAL'
);

const PublicOwnerDocumentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadBusy, setDownloadBusy] = useState(false);
  const autoDownloadTriggered = useRef(false);

  const filename = useMemo(() => buildOwnerTransactionFilename(transaction), [transaction]);
  const shouldAutoDownload = searchParams.get('download') === '1';
  const reversedDocument = isReversedDocument(transaction);

  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await axios.get(
          `${rootUrl}/api/public/owner-documents/${encodeURIComponent(token)}`
        );

        if (cancelled) {
          return;
        }

        if (response.data?.success) {
          setTransaction(response.data.data?.transaction || null);
          setCompanyInfo(response.data.data?.companyInfo || DEFAULT_COMPANY_INFO);
        } else {
          setError(response.data?.message || 'Failed to load document');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load document');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (token) {
      loadDocument();
    } else {
      setLoading(false);
      setError('Invalid share link');
    }

    return () => {
      cancelled = true;
    };
  }, [token]);

  const downloadPdf = async () => {
    if (!transaction) return;

    try {
      setDownloadBusy(true);
      const pdf = await generateOwnerTransactionPdf();
      pdf.save(filename);
    } finally {
      setDownloadBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shouldAutoDownload || autoDownloadTriggered.current || !transaction) {
      return;
    }

    autoDownloadTriggered.current = true;
    const timer = window.setTimeout(() => {
      downloadPdf().catch(() => {});
    }, 300);

    return () => window.clearTimeout(timer);
  }, [shouldAutoDownload, transaction]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-12">
        <Card className="mx-auto max-w-3xl border-0 shadow-lg">
          <CardBody className="p-8 text-center">
            <Typography variant="h5" className="text-[#2a2e30]">
              Loading document...
            </Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-12">
        <Card className="mx-auto max-w-3xl border-0 shadow-lg">
          <CardBody className="p-8 text-center">
            <Typography variant="h5" className="text-red-700">
              {error}
            </Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-3 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Card className="border-0 shadow-xl">
          <CardBody className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Typography variant="h4" className="text-[#2a2e30]">
                  {getDocumentLabel(transaction)}
                </Typography>
                <Typography variant="small" className="text-gray-500">
                  Public document link valid for 7 days
                </Typography>
                {reversedDocument && (
                  <div className="mt-3 inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-700">
                    Reversed document. Not a valid active document.
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={downloadPdf}
                  disabled={downloadBusy}
                  className="border border-[#0f766e] bg-white text-[#0f766e] shadow-sm hover:bg-[#ecfdf5]"
                  style={{ backgroundColor: '#ffffff', color: '#0f766e' }}
                >
                  {downloadBusy ? 'Preparing...' : 'Download PDF'}
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
              <DocumentPreviewScaler>
                <OwnerTransactionPrintSheet transaction={transaction} companyInfo={companyInfo} />
              </DocumentPreviewScaler>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default PublicOwnerDocumentPage;
